import { useCallback, useEffect } from "react";

import type {
  ViewerActionParams,
  ViewerActionType,
  ViewerEventPayloads,
  ViewerEventType,
} from "@situm/sdk-js";

import {
  AppUI,
  ARStatusTypes,
  type HostColorScheme,
  setARStatus,
  setHostColorSchemePreference,
  setLocationPickerInitialPosition,
  setUIMode,
  setUserSettingsPanelOpen,
  toggleUserSettingsPanelOpen,
} from "@/features/app/appSlice";
import {
  type Calibration,
  type GroundTruth,
  resetNewCalibrationGts,
  setLocalCalibrations,
  setNewCalibrationShowDialog,
} from "@/features/calibration/calibrationSlice";
import {
  setCurrentFilterText,
  setCurrentPoiCategoriesIDs,
  setFavoritePoisFilteringByBuilding,
  unsetCurrentPoiID,
} from "@/features/cartography/cartographySlice";
import {
  selectConfigItem,
  setConfigItem,
  setZoom,
} from "@/features/config/configSlice";
import {
  setMaxZoomLimit,
  setMinZoomLimit,
  setTrajectory,
} from "@/features/map/mapSlice";
import { setLocationStatus } from "../location/locationSlice";
import { setUserLanguage } from "../userPreferences/userPreferencesSlice";

import type { AppDispatch } from "@/app/store";
import {
  handleDirectionsRequestFromAPI,
  onDirectionsCanceled,
  onDirectionsOptionsSet,
} from "@/features/directions/actions";
import { selectFlightById } from "@/features/flight_information/actions";
import { handleLocationUpdate } from "@/features/location/actions";
import {
  handleExternalFeature,
  handleFollowUser,
  handleSetCamera,
  resetSession,
  resetViewState,
} from "@/features/map/actions";
import type { OnNavigationStartRequestedPayload } from "@/features/navigation/types";
import { mapLocationFromSDK } from "@/features/utils/sdkMapper";
import { sendMessage } from "@/features/webview_api/utils";
import {
  type Building,
  type Camera,
  type ExternalFeature,
  FIND_MY_CAR_LOCATION_ID,
  type Floor,
  type Location,
  NavigationStatus,
  type Poi,
  type PoiCategory,
  type Trajectory,
} from "@/libs/models";
import store from "../../app/store";
import { SUPPORTED_LANGUAGES } from "../app/constants";
import { isValidHostColorScheme } from "../app/hooks/theme/themeDetection";
import {
  handleDeselectPoi,
  handleRemoveCurrentPoiCategories,
  handleSelectFloor,
  handleSelectPoi,
  handleSetBuilding,
  handleSetCurrentPoiCategory,
} from "../cartography/actions";
import { handleAuthChange } from "../config/actions";
import {
  type ConfigState,
  FontSizeEnum,
  type FontSizeItem,
  FontSizeListArray,
} from "../config/types";
import { handleSaveFMC } from "../find_my_car/actions";
import type { ExternalLocation } from "../location/libs/location";
import { MAX_ZOOM, MIN_ZOOM } from "../map/constants";
import {
  mapCanceledPayload,
  mapNativeNavigationUpdatePayload,
} from "../native_routes/actions";
import { useNativeRoutes } from "../native_routes/NativeRoutesProvider";
import {
  cancelNavigation,
  onNavigationStartRequested,
  onNavigationToPointRequested,
} from "../navigation/actions";
import { NativeNavigationUpdateTypes } from "../navigation/types";
import { setReceivedSessionId } from "../share_location";
import { useSitumAPIContext } from "../situm_api/SitumAPIProvider";
import type { DotNotationKeys } from "../utils/genericity";
import {
  LocationClearStatusTypes,
  LocationStatusTypes,
  PermissionErrorTypes,
} from "../utils/types";
import {
  type CartographySelectionOptions,
  type NavigationStartPayload,
  type PostMessageErrorPayload,
  type ShareLocationPayload,
  type VibratePayload,
  ViewerActions,
  ViewerEvents,
} from "./types";

export { type ViewerActions as actions, ViewerEvents as events };
/**
 * Interface implementation
 */
export const WebviewApi = {
  /* Send new location to sdk */
  externalLocationAdd: (location: ExternalLocation): void => {
    sendMessage(ViewerEvents.LOCATION_EXTERNAL_ADD, location);
  },
  /* Start positioning using simulated positions */
  externalLocationStart: (buildingId: number): void => {
    sendMessage(ViewerEvents.LOCATION_EXTERNAL_START, {
      buildingIdentifier: buildingId,
    });
  },
  favoritePoisUpdated: (favoritePois: number[]): void => {
    sendMessage(ViewerEvents.FAV_POIS_UPDATED, {
      currentPoiIdentifiers: favoritePois,
      favoritePois,
    });
  },
  /**
   * The user asks for location to start
   */
  locationStart: (buildingId: number): void => {
    sendMessage(ViewerEvents.LOCATION_START, {
      buildingIdentifier: buildingId,
    });
  },
  /* Stop positioning session */
  locationStop: (): void => {
    sendMessage(ViewerEvents.LOCATION_STOP);
  },
  nativeNavigationStarted: (): void => {
    if (store.getState().config.internal.disableNavigationEvents) return;

    const payload = mapNativeNavigationUpdatePayload(
      NativeNavigationUpdateTypes.STARTED,
    );

    if (!payload) return; // Avoid sending empty payloads

    sendMessage(ViewerEvents.VIEWER_NAVIGATION_STARTED, payload);
  },
  nativeNavigationStopped: (): void => {
    if (store.getState().config.internal.disableNavigationEvents) return;

    const payload = mapCanceledPayload();
    sendMessage(ViewerEvents.VIEWER_NAVIGATION_STOPPED, payload);
  },
  nativeNavigationUpdated: (type: NativeNavigationUpdateTypes): void => {
    if (store.getState().config.internal.disableNavigationEvents) return;

    const payload = mapNativeNavigationUpdatePayload(type);

    if (!payload) return; // Avoid sending empty payloads

    sendMessage(ViewerEvents.VIEWER_NAVIGATION_UPDATED, payload);
  },
  onAuthIsReady: (): void => {
    sendMessage(ViewerEvents.READY_FOR_AUTH);
  },
  //SENDERS - Send events FROM the mapviewer to the integrator
  onBuildingSelected: (id: Building["id"] | null): void => {
    sendMessage(ViewerEvents.BUILDING_SELECTED, { identifier: id });
  },
  /**
   * The user added a new point to the calibration
   */
  onCalibrationPointClicked: (payload: GroundTruth) => {
    sendMessage(ViewerEvents.CALIBRATION_POINT_CLICKED, payload);
  },
  /**
   * The user requested to stop the calibration process
   */
  onCalibrationStopped: (payload: {
    status: "cancelled" | "undo" | "success";
  }) => {
    sendMessage(ViewerEvents.CALIBRATION_STOPPED, payload);
  },
  onCarSaved: (fmcData: {
    coordinates: { lat: number; lng: number };
    floorId: number;
  }): void => {
    sendMessage(ViewerEvents.FMC_SAVED, {
      coordinate: {
        latitude: fmcData.coordinates.lat,
        longitude: fmcData.coordinates.lng,
      },
      floorIdentifier: fmcData.floorId,
    });
  },
  onError: (payload: PostMessageErrorPayload): void => {
    sendMessage(ViewerEvents.APP_ERROR, payload);
  },
  onFloorSelected: (
    id: Floor["id"],
    buildingIdentifier: Building["id"],
  ): void => {
    sendMessage(ViewerEvents.FLOOR_SELECTED, {
      buildingIdentifier: buildingIdentifier,
      identifier: id,
    });
  },
  /**
   * The user clicked the button to upload the remaining local calibrations in the device
   */
  onLocalCalibrationsUploadRequested: () => {
    sendMessage(ViewerEvents.LOCAL_CALIBRATIONS_UPLOAD_REQUESTED);
  },
  onLocationPickerDragged: (
    payload: ViewerEventPayloads[ViewerEventType.LOCATION_PICKER_DRAGGED],
  ): void => {
    sendMessage(ViewerEvents.LOCATION_PICKER_DRAGGED, payload);
  },
  onMapClicked: (
    payload: ViewerEventPayloads[ViewerEventType.MAP_CLICKED],
  ): void => {
    sendMessage(ViewerEvents.MAP_CLICKED, payload);
  },

  //send events (like asking for directions) and notify some events done by the user
  onMapReady: (): void => {
    sendMessage(ViewerEvents.MAP_IS_READY);
  },
  onPoiCategoryDeselected: (ids: PoiCategory["id"][] | null): void => {
    sendMessage(ViewerEvents.POI_CATEGORY_DESELECTED, { identifiers: ids });
  },
  onPoiCategorySelected: (ids: PoiCategory["id"][] | null): void => {
    sendMessage(ViewerEvents.POI_CATEGORY_SELECTED, { identifiers: ids });
  },
  onPoiDeselected: (
    id: Poi["id"],
    buildingIdentifier: Building["id"],
  ): void => {
    sendMessage(ViewerEvents.POI_DESELECTED, {
      buildingIdentifier: buildingIdentifier,
      identifier: id,
    });
  },
  onPoiSelected: (id: Poi["id"], buildingIdentifier: Building["id"]): void => {
    sendMessage(ViewerEvents.POI_SELECTED, {
      buildingIdentifier: buildingIdentifier,
      identifier: id,
    });
  },
  onShareLocationStarted: (payload: ShareLocationPayload): void => {
    sendMessage(ViewerEvents.SHARE_LOCATION_STARTED, payload);
  },
  onShareLocationStopped: (): void => {
    sendMessage(ViewerEvents.SHARE_LOCATION_STOPPED);
  },
  onSpeakAloudText: (
    text: string,
    config: {
      lang?: string | undefined;
      rate?: number;
      volume?: number;
      pitch?: number;
    },
  ) => {
    const params = { text: text } as any;
    if (config["lang"]) params["lang"] = config["lang"];
    if (config["rate"]) params["rate"] = config["rate"];
    if (config["volume"]) params["volume"] = config["volume"];
    if (config["pitch"]) params["pitch"] = config["pitch"];

    sendMessage(ViewerEvents.SPEAK_ALOUD_TEXT, params);
  },
  onVibrate: (payload: VibratePayload): void => {
    sendMessage(ViewerEvents.APP_VIBRATE, payload);
  },
  startAR: (): void => {
    sendMessage(ViewerEvents.AR_START);
  },
  stopAR: (): void => {
    sendMessage(ViewerEvents.AR_STOP);
  },
};

// LISTENERS - Read actions sent TO the map viewer and update state accordingly
export const useWebviewApiListener = (dispatch: AppDispatch) => {
  const nativeRoutesContext = useNativeRoutes();
  const situmAPIContext = useSitumAPIContext();

  const processIncomingMessage = useCallback(
    (m: MessageEvent<{ type: ViewerActionType; payload: any }>) => {
      const channelsMap = {
        [ViewerActions.SET_CONFIG_ITEM]: (
          payload: [{ key: DotNotationKeys<ConfigState>; value: any }],
        ) => {
          payload?.forEach((p) => {
            if (!p.key || p.value == null || p.value === undefined) return;
            if (selectConfigItem(p.key) != null) {
              dispatch(setConfigItem(p.key, p.value));
            }
          });
        },
        [ViewerActions.APP_SET_AUTH]: (payload: {
          apikey?: string;
          jwt?: string;
        }) => {
          handleAuthChange(payload);
        },
        [ViewerActions.UI_SET_PREFERRED_THEME]: (payload: {
          theme: HostColorScheme | null;
        }) => {
          if (!isValidHostColorScheme(payload.theme)) {
            console.error(
              "Invalid payload for UI_SET_PREFERRED_THEME",
              payload,
            );
            return;
          }

          dispatch(setHostColorSchemePreference(payload.theme));
        },
        [ViewerActions.FOLLOW_USER]: (
          payload: { value: boolean } | boolean,
        ) => {
          handleFollowUser(payload);
        },

        [ViewerActions.SHOW_USER_SETTINGS]: (payload: boolean) => {
          dispatch(setUserSettingsPanelOpen(payload));
        },
        [ViewerActions.TOGGLE_USER_SETTINGS]: () => {
          dispatch(toggleUserSettingsPanelOpen());
        },
        [ViewerActions.CAMERA_SET]: (payload: Camera) => {
          handleSetCamera(payload);
        },
        [ViewerActions.SESSION_RESET]: () => {
          resetSession();
        },
        [ViewerActions.SELECT_POI]: (
          payload: ViewerActionParams[ViewerActionType.SELECT_POI],
        ) => {
          if ("identifier" in payload && "externalIdentifier" in payload) {
            console.error(
              "Invalid payload for SELECT_POI: both identifier and externalIdentifier are present",
              payload,
            );
            return;
          } else if ("identifier" in payload) {
            const poiId = Number(payload.identifier);
            if (!Number.isNaN(poiId)) {
              handleSelectPoi({ poiId }, situmAPIContext);
            }
          } else if ("externalIdentifier" in payload) {
            handleSelectPoi(
              { externalPoiId: payload?.externalIdentifier },
              situmAPIContext,
            );
          } else {
            console.error("Invalid payload for SELECT_POI", payload);
          }
        },
        [ViewerActions.DESELECT_POI]: (
          _payload: ViewerActionParams[ViewerActionType.DESELECT_POI],
        ) => {
          handleDeselectPoi();
        },
        [ViewerActions.SELECT_CAR]: () => {
          // Deprecated
          handleSelectPoi({ poiId: FIND_MY_CAR_LOCATION_ID }, situmAPIContext);
        },
        [ViewerActions.FMC_SELECT]: () => {
          handleSelectPoi({ poiId: FIND_MY_CAR_LOCATION_ID }, situmAPIContext);
        },
        [ViewerActions.FMC_SAVE]: (payload: {
          coordinate: { latitude: number; longitude: number };
          floorIdentifier: number;
        }) => {
          const {
            floorIdentifier: floorId,
            coordinate: { latitude: lat, longitude: lng },
          } = payload;
          handleSaveFMC({
            coordinates: { lat, lng },
            floorId,
          });
        },
        [ViewerActions.SELECT_POI_CATEGORY]: (
          payload: ViewerActionParams[ViewerActionType.SELECT_POI_CATEGORY],
        ) => {
          if ("identifier" in payload && "externalIdentifier" in payload) {
            console.error(
              "Invalid payload for SELECT_POI_CATEGORY: both identifier and externalIdentifier are present",
              payload,
            );
            return;
          } else if ("identifier" in payload) {
            handleSetCurrentPoiCategory({ poiCategoryId: payload?.identifier });
          } else if ("externalIdentifier" in payload) {
            handleSetCurrentPoiCategory({
              externalPoiCategoryId: payload?.externalIdentifier,
            });
          } else {
            console.error("Invalid payload for SELECT_POI_CATEGORY", payload);
          }
        },
        [ViewerActions.DESELECT_POI_CATEGORIES]: () => {
          handleRemoveCurrentPoiCategories();
        },
        [ViewerActions.BUILDING_SELECT]: (payload: { identifier: number }) => {
          handleSetBuilding(payload?.identifier);
        },
        [ViewerActions.FLOOR_SELECT]: (payload: {
          identifier: number;
          options?: CartographySelectionOptions;
        }) => {
          handleSelectFloor(
            payload?.identifier,
            payload?.options?.fitCamera ?? false,
          );
        },
        [ViewerActions.LOCATION_UPDATE]: (payload: any) => {
          // This message is received on every location update.
          mapLocationFromSDK(payload);
          handleLocationUpdate(payload);
        },
        [ViewerActions.LOCATION_UPDATE_STATUS]: (payload: any) => {
          const missingPemission = Object.values(PermissionErrorTypes).find(
            (ls) => ls === payload?.status,
          );
          if (payload.status === "CALCULATING") payload.status = "STARTING"; // Patch for older versions of RN plugin

          const statusReceived = missingPemission
            ? LocationStatusTypes.NO_PERMISSIONS
            : Object.values(LocationStatusTypes).find(
                (ls) => ls === payload?.status,
              );

          if (statusReceived) {
            dispatch(setLocationStatus(statusReceived));
            if (statusReceived === LocationStatusTypes.OOB) {
              // When we are OOB only update the state but keep the last location fixed
              handleLocationUpdate({
                ...store.getState().location.internalCurrentLocation,
                status: statusReceived,
              } as Location);
            } else if (statusReceived in LocationClearStatusTypes) {
              // TODO: probably this should not be necessary.
              // Send an empty location (just contains the status) so the location
              // gets cleared when needed.
              handleLocationUpdate({ status: statusReceived } as Location);
            }
          }
        },
        [ViewerActions.MAP_EXTERNAL_FEATURES]: (payload: ExternalFeature[]) => {
          handleExternalFeature(payload);
        },
        [ViewerActions.MAP_TRAJECTORY]: (payload: Trajectory) => {
          store.dispatch(setTrajectory(payload));
        },
        [ViewerActions.NAVIGATION_START]: (payload: NavigationStartPayload) => {
          if (
            "lat" in payload &&
            "lng" in payload &&
            "floorIdentifier" in payload
          ) {
            // Navigation to a custom point
            onNavigationToPointRequested(
              payload,
              nativeRoutesContext.routesCalculator,
            );
          } else {
            // Navigation to a POI or query with filter
            void onNavigationStartRequested(
              payload as OnNavigationStartRequestedPayload,
              situmAPIContext,
            );
          }
        },
        [ViewerActions.NAVIGATION_TO_CAR]: (payload: any) => {
          void onNavigationStartRequested(
            {
              ...payload,
              navigationTo: FIND_MY_CAR_LOCATION_ID,
            },
            situmAPIContext,
          );
        },
        [ViewerActions.NAVIGATION_CANCEL]: () => {
          cancelNavigation();
        },
        [ViewerActions.DIRECTIONS_START]: (
          payload: ViewerActionParams[ViewerActionType.DIRECTIONS_START],
        ) => {
          handleDirectionsRequestFromAPI(payload);
        },
        [ViewerActions.DIRECTIONS_SET_OPTIONS]: (payload: any) => {
          onDirectionsOptionsSet(payload);
        },
        [ViewerActions.DIRECTIONS_CANCEL]: () => {
          onDirectionsCanceled();
        },
        [ViewerActions.SELECT_FLIGHT]: (
          payload: ViewerActionParams[ViewerActionType.SELECT_FLIGHT],
        ) => {
          selectFlightById(payload.flightId);
        },
        [ViewerActions.INITIAL_CONFIG]: (payload: any) => {
          //enablePoiClustering
          payload?.enablePoiClustering === false &&
            dispatch(setConfigItem("poi.spacingFactor", 10000));
          //showPoiNames
          payload.showPoiNames !== undefined &&
            payload.showPoiNames !== null &&
            dispatch(
              setConfigItem(
                "ui.widget_visibility.poi_names",
                payload.showPoiNames,
              ),
            );
          //minZoom
          if (
            payload?.minZoom >= MIN_ZOOM &&
            (payload?.maxZoom ? payload?.maxZoom >= payload?.minZoom : true)
          ) {
            dispatch(setMinZoomLimit(payload.minZoom));
            dispatch(setConfigItem("camera.minZoom", payload.minZoom));
          }
          //maxZoom
          if (
            payload?.maxZoom <= MAX_ZOOM &&
            (payload?.minZoom ? payload?.maxZoom >= payload?.minZoom : true)
          ) {
            dispatch(setMaxZoomLimit(payload.maxZoom));
            dispatch(setConfigItem("camera.maxZoom", payload.maxZoom));
          }
          //initialZoom
          payload?.initialZoom >= MIN_ZOOM &&
            payload?.initialZoom <= MAX_ZOOM &&
            dispatch(setZoom(payload.initialZoom));
          if (payload.error) {
            console.error("error loading initial config: ", payload.error);
          }
        },
        [ViewerActions.LANGUAGE_CONFIG]: (payload: any) => {
          payload &&
            SUPPORTED_LANGUAGES.includes(payload) &&
            dispatch(setUserLanguage(payload));
        },
        [ViewerActions.FONT_SIZE_UPDATE]: (payload: { size: FontSizeItem }) => {
          let value = payload.size;
          if (payload.size.startsWith("a-")) {
            value = FontSizeEnum.XXXL as FontSizeItem;
          }
          if (!FontSizeListArray.includes(value as FontSizeItem)) {
            console.error(`Invalid font size '${value}' selected`);
            return;
          }
          payload &&
            dispatch(setConfigItem("ui.fontSize", value as FontSizeItem));
        },
        [ViewerActions.SET_FAV_POIS]: (payload: any) => {
          payload && dispatch(setFavoritePoisFilteringByBuilding(payload));
        },
        [ViewerActions.SET_UI_MODE]: (payload: any) => {
          if (!payload) {
            console.error("Argument 'mode' is needed");
            return;
          }
          const newMode = AppUI[payload.mode as AppUI];
          if (!newMode) {
            console.error(`Invalid mode '${payload.mode}' selected`);
          } else {
            dispatch(setUIMode(newMode));
          }
        },
        [ViewerActions.SET_SEARCH_FILTER]: (payload: any) => {
          // If navigation has started ignore search petition.
          const navigationIsActive =
            store.getState().navigation.navigationStatus !==
            NavigationStatus.STOPPED;
          if (navigationIsActive) return;

          // Deselect the current selected POI
          dispatch(unsetCurrentPoiID());

          const { text, poiCategoryIdentifier } = payload;

          if (text !== undefined && typeof text == "string") {
            dispatch(setCurrentFilterText(text));
          }

          if (poiCategoryIdentifier === undefined) return;

          if (
            typeof poiCategoryIdentifier == "string" &&
            poiCategoryIdentifier == ""
          ) {
            dispatch(setCurrentPoiCategoriesIDs(null));
            return;
          }

          const category = store
            .getState()
            .app.poiCategories.findCategoryById(
              parseInt(poiCategoryIdentifier),
            );

          if (!category) return;
          handleSetCurrentPoiCategory({ poiCategoryId: category.id });
        },
        [ViewerActions.AR_UPDATE_STATUS]: (payload: { type: string }) => {
          if (!store.getState().config.feature_flags.ar_integration) return;

          switch (payload.type) {
            case "success":
              dispatch(setARStatus(ARStatusTypes.started));
              break;
            case "error":
            case "finished":
              dispatch(setARStatus(ARStatusTypes.shown));
              break;
          }
        },
        [ViewerActions.SET_LOCAL_CALIBRATIONS]: (payload: {
          calibrations: Calibration[];
        }) => {
          store.dispatch(setLocalCalibrations(payload.calibrations));
        },
        [ViewerActions.SHARE_LOCATION_SET_IDENTIFIER]: (payload: {
          identifier: string;
        }) => {
          store.dispatch(setReceivedSessionId(payload?.identifier));
        },
        [ViewerActions.STOP_CALIBRATION]: (payload: {
          status?: "cancelled" | "undo" | "success";
        }) => {
          if (!payload.status) {
            store.dispatch(setNewCalibrationShowDialog(true));
          } else {
            store.dispatch(resetNewCalibrationGts());
            store.dispatch(setNewCalibrationShowDialog(false));
            // sendMessage(ViewerEvents.CALIBRATION_STOPPED, payload);
          }
        },
        [ViewerActions.OPEN_LOCATION_PICKER]: (
          payload: ViewerActionParams[ViewerActionType.OPEN_LOCATION_PICKER],
        ) => {
          if (payload != null) {
            if ("initialPosition" in payload) {
              store.dispatch(
                setLocationPickerInitialPosition(payload.initialPosition),
              );
            } else if ("poiIdentifier" in payload) {
              handleSelectPoi(
                { poiId: payload.poiIdentifier },
                situmAPIContext,
              );
            } else if ("poiExternalIdentifier" in payload) {
              handleSelectPoi(
                { externalPoiId: payload.poiExternalIdentifier },
                situmAPIContext,
              );
            }
          }
          dispatch(setUIMode(AppUI.locationPicker));
        },
      };

      const action = channelsMap[m.data.type as ViewerActionType];

      action?.(m.data.payload);
    },
    [dispatch, nativeRoutesContext, situmAPIContext],
  );

  useEffect(() => {
    window.addEventListener("message", processIncomingMessage);
    return () => {
      window.removeEventListener("message", processIncomingMessage);
    };
  }, [processIncomingMessage]);
};
